import React, { useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import {
    format,
    addMonths,
    subMonths,
    startOfMonth,
    endOfMonth,
    startOfWeek,
    endOfWeek,
    eachDayOfInterval,
    isSameMonth,
    isSameDay
} from 'date-fns';

export default function AdminDashboardCalendar({ events = [], onEventClick }) {
    const [currentDate, setCurrentDate] = useState(new Date());

    const nextMonth = () => setCurrentDate(addMonths(currentDate, 1));
    const prevMonth = () => setCurrentDate(subMonths(currentDate, 1));
    const goToToday = () => setCurrentDate(new Date());

    const monthStart = startOfMonth(currentDate);
    const monthEnd = endOfMonth(currentDate);
    const startDate = startOfWeek(monthStart, { weekStarts: 0 }); // Sunday start
    const endDate = endOfWeek(monthEnd, { weekStarts: 0 });

    const daysInGrid = eachDayOfInterval({ start: startDate, end: endDate });

    // Days of the week header
    const weekDays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

    return (
        <div className="admin-dashboard-calendar-container" style={{
            background: 'var(--bg-card, #ffffff)',
            borderRadius: '16px',
            border: '1px solid var(--border)',
            boxShadow: '0 4px 12px rgba(0,0,0,0.03)',
            overflow: 'hidden'
        }}>
            {/* Header */}
            <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '20px',
                borderBottom: '1px solid var(--border)'
            }}>
                <h3 style={{
                    fontSize: '1.25rem',
                    fontWeight: '700',
                    color: 'var(--text-primary)'
                }}>
                    {format(currentDate, 'MMMM yyyy')}
                </h3>
                
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <button
                        onClick={goToToday}
                        style={{
                            padding: '6px 14px',
                            fontSize: '0.85rem',
                            fontWeight: '600',
                            borderRadius: '8px',
                            border: '1px solid var(--border)',
                            background: 'white',
                            cursor: 'pointer',
                            color: 'var(--text-secondary)'
                        }}
                    >
                        Today
                    </button>
                    <div style={{ display: 'flex', gap: '4px' }}>
                        <button
                            onClick={prevMonth}
                            style={{
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                width: '32px', height: '32px', borderRadius: '8px',
                                border: '1px solid var(--border)', background: 'white',
                                cursor: 'pointer', color: 'var(--text-secondary)'
                            }}
                        >
                            <ChevronLeft size={16} />
                        </button>
                        <button
                            onClick={nextMonth}
                            style={{
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                width: '32px', height: '32px', borderRadius: '8px',
                                border: '1px solid var(--border)', background: 'white',
                                cursor: 'pointer', color: 'var(--text-secondary)'
                            }}
                        >
                            <ChevronRight size={16} />
                        </button>
                    </div>
                </div>
            </div>

            {/* Grid */}
            <div style={{ padding: '20px' }}>
                <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(7, 1fr)',
                    gap: '4px',
                    marginBottom: '8px'
                }}>
                    {weekDays.map(day => (
                        <div key={day} style={{
                            textAlign: 'center',
                            fontSize: '0.85rem',
                            fontWeight: '700',
                            color: 'var(--text-tertiary)',
                            textTransform: 'uppercase',
                            paddingBottom: '8px'
                        }}>
                            {day}
                        </div>
                    ))}
                </div>

                <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(7, 1fr)',
                    gap: '4px'
                }}>
                    {daysInGrid.map((day, idx) => {
                        const dayEvents = events.filter(e => e.date && isSameDay(new Date(e.date), day));
                        
                        // Limit to 3 events per day visually
                        const displayEvents = dayEvents.slice(0, 3);
                        const hasMore = dayEvents.length > 3;

                        const isCurrentMonth = isSameMonth(day, currentDate);
                        const isToday = isSameDay(day, new Date());

                        return (
                            <div key={idx} style={{
                                minHeight: '100px',
                                border: '1px solid rgba(0,0,0,0.04)',
                                borderRadius: '8px',
                                padding: '8px',
                                background: isCurrentMonth ? 'rgba(255,255,255,0.8)' : 'rgba(249,250,251,0.5)',
                                opacity: isCurrentMonth ? 1 : 0.6,
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '4px',
                                transition: 'all 0.2s ease',
                                cursor: 'pointer' // Can add functionality to filter the main events list by this day later
                            }}>
                                <div style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    marginBottom: '4px'
                                }}>
                                    <span style={{
                                        fontSize: '0.85rem',
                                        fontWeight: isToday ? '700' : '600',
                                        color: isToday ? 'white' : 'var(--text-secondary)',
                                        background: isToday ? 'var(--primary, #e81cff)' : 'transparent',
                                        width: '24px',
                                        height: '24px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        borderRadius: '50%'
                                    }}>
                                        {format(day, 'd')}
                                    </span>
                                </div>
                                
                                {/* Event Indicators */}
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                    {displayEvents.map((evt, i) => (
                                        <div key={i} 
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                if (onEventClick) onEventClick(evt);
                                            }}
                                            style={{
                                                fontSize: '0.7rem',
                                                padding: '4px 6px',
                                                background: 'rgba(232, 28, 255, 0.1)',
                                                color: '#b010c2',
                                                borderRadius: '4px',
                                                whiteSpace: 'nowrap',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                fontWeight: '600',
                                                cursor: 'pointer'
                                            }} title={evt.title}>
                                            {format(new Date(evt.date), 'HH:mm')} • {evt.title}
                                        </div>
                                    ))}
                                    {hasMore && (
                                        <div style={{
                                            fontSize: '0.65rem',
                                            color: 'var(--text-tertiary)',
                                            fontWeight: '600',
                                            paddingLeft: '2px'
                                        }}>
                                            +{dayEvents.length - 3} more
                                        </div>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
}
